import React from "react";
import {RouterContext} from "../router";
import KeycloakApi from "../../lib/keycloak";
import {config, N8N} from "../index";
import {Notification} from "../components/popups";
import FileDrop from "../components/file-drop";

export interface Kickoff {
	unternehmen: string,
	branche: string,
	zielgruppe: string,
	hauptziel: string,
	markenwirkung: string,
	preispositionierung: string,
	vertrauen: string,
	pflichtinhalte: string,
	mobile_prioritaet: string,
	no_gos: string,
	website_url: string,
	design_strikt: boolean
}

export default function Kickoff(props: { keycloak: KeycloakApi }) {
	const router = React.useContext(RouterContext);
	const n8n = React.useContext(N8N);
	const ref = React.useRef<HTMLFormElement>(null);

	const kickoff = React.useCallback(async (data: FormData) => {
		const payload: Kickoff = Object.fromEntries(data) as any;
		if (props.keycloak.access)
			await n8n.kickoff(payload)
				.then(() => router?.navigate("/kickoff/done", Object.fromEntries(data)))
				.catch(e => {
					console.error(e);
					Notification({
						heading: 'An error occurred',
						style: 'error',
						body: <pre>
							<code>
								{e.toString() ?? JSON.stringify(e, null, 4)}
							</code>
						</pre>
					})
				});
	}, []);

	const drop = React.useCallback(async function (file: File) {
		const data: Partial<Kickoff> = JSON.parse(await file.text());
		const form = ref.current;
		if (!form) return;

		for (const [key, value] of Object.entries(data)) {
			const el = form.elements.namedItem(key);
			if (!el) continue;

			if (el instanceof HTMLInputElement && el.type === "checkbox")
				el.checked = Boolean(value);
			else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)
				el.value = String(value);
		}
	}, []);

	return <section id='kickoff'>
		<article id="overview">
			<h1>Kundenaufnahme Portal</h1>

			<p>Hier Kundenaufnahmedatein eingeben. Alternativ eine Aquisedatei hochladen.</p>

		</article>

		<FileDrop onDrop={drop} accept={".json"}>
			<span data-icon={'\uf864'}>Vorausfüllen</span>
		</FileDrop>

		<form ref={ref} action={kickoff}>
			<label htmlFor="unternehmen">Unternehmen</label>
			<input id="unternehmen" name="unternehmen" placeholder='AkroServices KGSM GbR'/>
			<span className='description'>Der Name des Unternehmens</span>

			<hr/>

			<label htmlFor="branche">Branche</label>
			<input id="branche" name="branche" placeholder='Web- und Mediendienste; Internet und Web Services'/>
			<span className='description'>In welcher Branche ist das Unternehmen hauptsächlich tätig? Sowohl Überbegriffe als auch spezifischere Unterbereiche reichen aus.</span>

			<hr/>

			<label htmlFor="zielgruppe">Zielgruppe</label>
			<input id="zielgruppe" name="zielgruppe" placeholder='Unternehmen, die ihre Webpräsenz nicht in Fokus setzen möchten.'/>
			<span className='description'>Für wen soll die Webseite geschneidert sein?</span>

			<hr/>

			<label htmlFor="hauptziel">Hauptziel</label>
			<input id="hauptziel" name="hauptziel" placeholder='Den Verkauf von Webseiten mit Fokus auf DSVGO Konformität.'/>
			<span className='description'>Was soll die Webseite erreichen? (z.B. Neukunden, Informationen über neuigkeiten, Produktverkauf, Geschäftserweiterung, etc.)</span>

			<hr/>

			<label htmlFor="markenwirkung">Markenwirkung</label>
			<textarea id="markenwirkung" name="markenwirkung" placeholder='Persönlich, aber kompetent.'/>
			<span className='description'>Das gewünschte Erscheinungsbild der Marke. (z.B. Professionalität, Zügigkeit, Volumengeschäft, Handwerk, Seriös, Persönlich, etc)</span>

			<hr/>

			<label htmlFor="preispositionierung">Preispositionierung</label>
			<textarea id="preispositionierung" name="preispositionierung"/>
			<span className='description'></span>

			<hr/>

			<label htmlFor="vertrauen">Vertrauen</label>
			<textarea id="vertrauen" name="vertrauen"/>
			<span className='description'></span>

			<hr/>

			<label htmlFor="pflichtinhalte">Pflichtinhalte</label>
			<textarea id="pflichtinhalte" name="pflichtinhalte" placeholder='Google Maps Karte zum Büro.' />
			<span className='description'>Was muss auf jeden Fall in die Webseite mit eingebaut werden?</span>

			<hr/>

			<label htmlFor="mobile_prioritaet">Mobile Priorität</label>
			<input id="mobile_prioritaet" name="mobile_prioritaet"/>
			<span className='description'>Sind Benutzer auf Mobilgeräte ein großer Fokus?</span>

			<hr/>

			<label htmlFor="no_gos">No-Gos</label>
			<textarea id="no_gos" name="no_gos" placeholder='Gesellschafterdaten'/>
			<span className='description'>Was darf auf gar keinen Fall in das Wireframe gelangen?</span>

			<hr/>

			<label htmlFor="website_url">Webseiten URL</label>
			<input id="website_url" name="website_url" type="url" placeholder='https://akroservices.de'/>
			<span className='description'></span>

			<hr/>

			<label htmlFor="design_strikt">Design strikt</label>
			<input type="checkbox" id="design_strikt" name="design_strikt" className="switch"/>
			<span className='description'></span>

			<div style={{ display: 'flex', justifyContent: 'flex-end', gridColumn: '1 / -1' }}>
				<button className='colour-green'>Erstellen</button>
			</div>
		</form>
	</section>;
}

export function Done() {
	if (!window.history.state)
		return <section className="action-buttons">
			<h1>Kickoff erfolgreich.</h1>
			<p>
				Die Daten wurden erfolgreich an n8n übermittelt.
				Allerdings wurden sie nicht zurückgegeben, und können nicht heruntergeladen werden.
			</p>
		</section>;

	const download = React.useCallback(() => {
		const text = new Blob([JSON.stringify(window.history.state, null, 4)], {
			type: 'application/json'
		});

		const url = URL.createObjectURL(text);
		const a = document.createElement('a');
		a.href = url;
		a.download = `Kickoff-${new Intl.DateTimeFormat('de', {dateStyle: 'medium'}).format(new Date())}.json`

		a.click();
	}, []);

	return <section className="action-buttons">
		<h1>Kickoff erfolgreich.</h1>
		<p>Die Daten im Formular können für zukünftige Aufrufe heruntergeladen werden.</p>

		<button onClick={() => download()}>Herunterladen</button>
	</section>;
}